import styled from 'styled-components'
import { QueryClient } from 'react-query'
import { useRouter } from 'next/router'
import { dehydrate } from 'react-query/hydration'
import Navigation from 'components/navigation'
import LessonsList from 'learning/lessons-path/list'
import ReviewCard from 'course/review/review-card'
import NotFound from 'errors/not-found'
import InternalError from 'errors/internal-error'
import OpenGraph from 'common/open-graph'
import {
  prefetchBadge, prefetchCourse, useBadge, useCourse,
} from 'querys/course'
import { getCourseListService, getCourseService } from 'services/course'
import { prefetchSubscriptionPlans } from 'querys/subscriptions'
import PricingBanner from 'courses/pricing-banner'
import { prefetchCourseLessons } from 'querys/lesson'
import LearningHero from 'learning/learning-hero'
import FinalProject from 'learning/final-project'
import CertificateCard from 'learning/certificate-card'
import flatEditorJsContent from 'utils/flat-editor-js-content'

const Spacer = styled.div`
  height: 112px;
  @media(min-width: 768px) {
    height: 88px;
  }
`

function Course({ courseSlug, openGraph }) {
  const router = useRouter()
  const queryCourse = useCourse()
  const { data, error, isLoading, isFetchedAfterMount } = queryCourse
  const { data: dataBadge } = useBadge()

  if (isLoading) return null
  if (router.isFallback) return <NotFound />

  if (error?.message === 'User Unauthorized') return null
  if (error?.message === 'Not Found') return <NotFound />
  if (error?.message === 'Internal Error') return <InternalError />

  const {
    course_progress, is_workshop, is_free, has_access, certificate,
  } = data

  const {
    title, description, url, image, keyword,
  } = openGraph

  return (
    <>
      <OpenGraph
        title={title}
        description={description}
        url={url}
        image={image}
        keyword={keyword}
      />
      <Navigation background="var(--sky)" />
      <LearningHero />
      <LessonsList />
      <FinalProject
        project={data.project}
        badge={dataBadge?.badge}
        course_progress={course_progress?.progress}
        isWorkShop={is_workshop}
        certificate={certificate}
      />
      <ReviewCard />
      <CertificateCard />
      <PricingBanner
        hidden={has_access || !isFetchedAfterMount}
        isFree={is_free}
        slug={courseSlug}
        opacity
        fixed
      />
      <Spacer />
    </>
  )
}

Course.pageTheme = {
  background: 'var(--sky)',
}

export async function getStaticPaths() {
  const courses = await getCourseListService()
  const paths = []
  courses.forEach(({ slug }) => paths.push({ params: { course: slug } }))
  return {
    paths,
    fallback: true,
  }
}

export async function getStaticProps({ params: { course } }) {
  const queryClient = new QueryClient()
  const data = await getCourseService({ course_slug: course })
  await prefetchCourse({ slug: course, client: queryClient })
  await prefetchCourseLessons({ queryClient, course })
  await prefetchBadge({ queryClient, course })
  await prefetchSubscriptionPlans({ queryClient, product: 'ALL' })
  return {
    props: {
      dehydratedState: dehydrate(queryClient),
      courseSlug: course,
      openGraph: {
        title: data?.title,
        description: flatEditorJsContent(data?.description),
        url: `https://leonidasesteban.com/cursos/${course}`,
        image: data?.featured_video?.thumbnails[2]?.link,
        keyword: data?.meta,
      },
    },
    revalidate: 3600,
  }
}

export default Course
